import React, { useState } from 'react';
import { X, FileText, ExternalLink } from 'lucide-react';
import { useFileApi } from '../../hooks/useFileApi';
import { FileViewer } from '../common/FileViewer';
import { BatchFileLogDTO } from '../../services/apiClient';

interface MandateListDrawerProps {
    memberName: string;
    mandates: BatchFileLogDTO[];
    isOpen: boolean;
    onClose: () => void;
}

const MandateListDrawer: React.FC<MandateListDrawerProps> = ({
    memberName,
    mandates,
    isOpen,
    onClose,
}) => {
    const { getFileUrl } = useFileApi();
    const [selectedFile, setSelectedFile] = useState<{ url: string; name: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleViewMandate = async (mandate: BatchFileLogDTO) => {
        if (!mandate.mandateFilePath) {
            alert('No mandate file path found.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await getFileUrl('mandates', mandate.mandateFilePath);
            if (response.success && response.data) {
                setSelectedFile({
                    url: response.data,
                    name: mandate.mandateFilePath.split('/').pop() || 'mandate.pdf'
                });
            } else {
                alert('Failed to load mandate file. Please try again.');
            }
        } catch (error) {
            console.error('Error loading mandate:', error);
            alert('Failed to load mandate file. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenMandate = async (mandate: BatchFileLogDTO) => {
        if (!mandate.mandateFilePath) {
            alert('No mandate file path found.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await getFileUrl('mandates', mandate.mandateFilePath);
            if (response.success && response.data) {
                // Open PDF in new tab
                window.open(response.data, '_blank');
            } else {
                alert('Failed to open mandate file. Please try again.');
            }
        } catch (error) {
            console.error('Error opening mandate:', error);
            alert('Failed to open mandate file. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (dateString?: string | Date) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-[5] transition-opacity"
                onClick={onClose}
            />

            {/* Drawer */}
            <div
                className={`fixed right-0 top-0 h-full w-full sm:w-2/3 lg:w-1/2 xl:w-1/3 bg-white dark:bg-gray-800 shadow-xl z-10 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : 'translate-x-full'
                    }`}
            >
                <div className="flex flex-col h-full">
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                Mandates
                            </h2>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {memberName}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            disabled={isLoading}
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {mandates.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                <FileText size={48} className="text-gray-400 mb-4" />
                                <p className="text-gray-600 dark:text-gray-400">
                                    No mandates found for this member.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {mandates.map((mandate) => (
                                    <div
                                        key={mandate.id}
                                        className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <FileText size={18} className="text-gray-500 dark:text-gray-400" />
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                                        Mandate
                                                    </span>
                                                </div>
                                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                                    {formatDate(mandate.createdUtc)}
                                                </p>
                                            </div>
                                        </div>

                                        {mandate.fileToken && (
                                            <div className="mb-3">
                                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                                                    File Token
                                                </p>
                                                <p className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                                                    {mandate.fileToken}
                                                </p>
                                            </div>
                                        )}

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleViewMandate(mandate)}
                                                disabled={isLoading}
                                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <FileText size={16} className="mr-2" />
                                                View
                                            </button>
                                            <button
                                                onClick={() => handleOpenMandate(mandate)}
                                                disabled={isLoading}
                                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 dark:text-green-300 dark:bg-green-900 dark:hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <ExternalLink size={16} className="mr-2" />
                                                Open
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* FileViewer for viewing individual mandate */}
            {selectedFile && (
                <FileViewer
                    fileName={selectedFile.name}
                    sasUrl={selectedFile.url}
                    onClose={() => setSelectedFile(null)}
                />
            )}
        </>
    );
};

export default MandateListDrawer;
